import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Brand } from "@/components/Brand";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export default function BookingReturn() {
  const navigate = useNavigate();
  const [state, setState] = useState("checking"); // checking | success | failed | timeout
  const attempts = useRef(0);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) { setState("failed"); return; }

    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      if (attempts.current >= 8) { setState("timeout"); return; }
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setState("success"); return; }
        if (data.status === "expired") { setState("failed"); return; }
      } catch { /* retry */ }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen flex items-center justify-center bg-primary/5 px-4">
      <div className="w-full max-w-md text-center">
        <Brand size="md" className="mb-8" />
        <div className="rounded-3xl bg-white border border-border p-10 shadow-xl shadow-primary/10">
          {state === "checking" && (
            <>
              <Loader2 className="mx-auto text-primary animate-spin" size={48} />
              <h1 className="font-display text-2xl mt-4">Confirming your payment...</h1>
              <p className="text-slate-500 mt-2">Please wait a moment.</p>
            </>
          )}
          {state === "success" && (
            <>
              <CheckCircle2 className="mx-auto text-success" size={56} />
              <h1 className="font-display text-3xl mt-4 text-slate-800">Booking Confirmed! 🎉</h1>
              <p className="text-slate-600 mt-2">
                Your account has been created and your child's place is reserved. Log in to view your Member Portal.
              </p>
              <Button onClick={() => navigate("/auth")} data-testid="return-login-btn"
                className="mt-6 rounded-full font-display text-lg bg-primary hover:bg-primary/90 px-8 py-6">
                Log In to Portal
              </Button>
            </>
          )}
          {(state === "failed" || state === "timeout") && (
            <>
              <XCircle className="mx-auto text-destructive" size={56} />
              <h1 className="font-display text-2xl mt-4">
                {state === "timeout" ? "Still processing..." : "Payment not completed"}
              </h1>
              <p className="text-slate-600 mt-2">
                {state === "timeout"
                  ? "Your payment is taking longer than usual. Please check your email for confirmation."
                  : "We couldn't confirm your payment. Please try booking again."}
              </p>
              <Button onClick={() => navigate("/book-online")} data-testid="return-retry-btn"
                className="mt-6 rounded-full font-display bg-secondary hover:bg-secondary/90 px-8 py-6">
                Back to Booking
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
